import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PACKAGE_INFORMATION } from '~/endpoints';
import {
  PackageInformationMutationRequest,
  PackageInformationMutationResponse,
  PackageInformationRequest,
  PackageInformationResponse,
} from '~/types/api';
import { handleErrorResponse } from '~/helpers/api';
import { PackageInformationMutationRequestSchema, PackageInformationMutationResponseSchema, PackageInformationResponseSchema } from '~/validation/api/packageInformationResponse';
import { MutationError } from '~/types/form';
import { defaultDialCode } from '../useDialOptions';

export const initialFormData = (
  sessionId: string,
  pax: number,
  locale: string,
): PackageInformationResponse => ({
  session_id: sessionId,
  main_booker: {
    email: '',
    phone_country_code: defaultDialCode(locale),
    phone: '',
    phone_trip_country_code: defaultDialCode(locale),
    phone_trip: '',
    passport_number: '',
  },
  address: {
    street: '',
    housenum: '',
    address_line_2: null,
    city: '',
    state: null,
    postal_code: '',
    country: '',
  },
  participants: Array(pax).fill(null).map((_, index) => ({
    index,
    gender: null,
    first_name: '',
    last_name: '',
    birth_date: '',
    medical_info: '',
    meal_preferences: '',
    preferences: '',
    passport_number: '',
  })),
  room_assignments: [],
  promo_code: '',
  agreement: false,
  newsletter: false,
});

const getInformationFormData = async (sessionId: string) => {
  try {
    const response = await axios.get(`${PACKAGE_INFORMATION}?session_id=${sessionId}`);

    const json = response.data;

    return PackageInformationResponseSchema.parse(json);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw await handleErrorResponse(error.response);
    }

    throw error;
  }
};

const updatePackageInformation = async (obj: PackageInformationMutationRequest) => {
  const body = PackageInformationMutationRequestSchema.parse(obj);

  try {
    const response = await axios.post(PACKAGE_INFORMATION, body);

    const json = response.data;

    return PackageInformationMutationResponseSchema.parse(json);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw await handleErrorResponse(error.response);
    }

    throw error;
  }
};

export function useInformationForm(sessionId: string) {
  return useQuery<PackageInformationRequest, Error, PackageInformationResponse>({
    queryKey: [
      'packageInformation',
      sessionId,
    ],
    queryFn: () => getInformationFormData(sessionId),
    staleTime: 1, // Stale time is 1 second
    retry: false,
    enabled: !!sessionId,
  });
}

export function useMutatePackageInformation() {
  // eslint-disable-next-line max-len
  return useMutation<PackageInformationMutationResponse, MutationError, PackageInformationMutationRequest>({
    mutationFn: (obj: PackageInformationMutationRequest) => {
      return updatePackageInformation(obj);
    },
  });
}
